import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  DragDropContext,
  Draggable,
  DraggingStyle,
  Droppable,
  DropResult,
  NotDraggingStyle,
} from 'react-beautiful-dnd';
import {
  Button,
  CircularProgress,
  Grid2 as Grid,
  Paper,
  Stack,
} from '@mui/material';
import { ChefHat, Search, CheckCheck, Truck } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import CardStage from './components/OrderCard';
import OrderStageHeader from '@/pages/organization-orders/components/OrderStageHeader';
import {
  useAddOrderMutation,
  useUpdateOrderStatusMutation,
} from '@/features/organizations/api/ordersApi';
import { setContainer } from '@/store/uiSlice';
import { useAppDispatch, useOrder } from '@/hooks';
import { Order as IOrder, OrderStatus } from '@/types';
import useSnackbar from '@/hooks/useSnackbar';
import { isEmpty } from 'lodash';
import OrderDetails from './components/OrderDetails';
import { fetchUsers } from '@/store/usersSlice';

type StageOrders = Record<string, IOrder[]>;

const STAGES = {
  [OrderStatus.REVIEWING]: {
    title: 'Revisando',
    icon: <Search />,
  },
  [OrderStatus.PREPARING]: {
    title: 'Preparando',
    icon: <ChefHat />,
  },
  [OrderStatus.SENDING]: {
    title: 'Enviando',
    icon: <Truck />,
  },
  [OrderStatus.DELIVERED]: {
    title: 'Entregado',
    icon: <CheckCheck />,
  },
};

const Orders = () => {
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();
  const { organizationId, orderId } = useParams<{
    organizationId: string;
    orderId: string;
  }>();
  const [localOrders, setLocalOrders] = useState<StageOrders>({});
  const [updateOrderStatus] = useUpdateOrderStatusMutation();
  const [addOrder] = useAddOrderMutation();
  const [isLoading, setIsLoading] = useState(true);

  const { orders, subscribeToOrdersByOrganizationId } = useOrder();

  useEffect(() => {
    if (!organizationId) return;
    setIsLoading(true)
    const unsubscribe = subscribeToOrdersByOrganizationId(organizationId)
    setIsLoading(false);
    return () => {
      unsubscribe();
    };
  }, [dispatch, organizationId, subscribeToOrdersByOrganizationId]);

  const getItemStyle = (
    isDragging: boolean,
    draggableStyle: DraggingStyle | NotDraggingStyle | undefined
  ) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    boxShadow: isDragging ? '0px 8px 16px rgba(0, 0, 0, 0.1)' : 'none',
    // change background colour if dragging
    background: 'white',
    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const onDragEnd = async ({ source, destination }: DropResult) => {
    if (!destination) return;
    if (source.droppableId !== destination.droppableId) {
      const sourceColumn = localOrders[source.droppableId];
      const destColumn = localOrders[destination.droppableId] ?? [];

      const [removedOrder] = sourceColumn.splice(source.index, 1);
      const updatedOrder = {
        ...removedOrder,
        status: destination.droppableId as OrderStatus,
      };
      destColumn.splice(destination.index, 0, updatedOrder);

      setLocalOrders((prev) => ({
        ...prev,
        [source.droppableId]: sourceColumn,
        [destination.droppableId]: destColumn,
      }));

      try {
        await updateOrderStatus({
          organizationId,
          orderId: updatedOrder.id,
          status: updatedOrder.status,
        });
      } catch (error) {
        destColumn.splice(destination.index, 1);
        sourceColumn.splice(source.index, 0, {
          ...updatedOrder,
          status: source.droppableId as OrderStatus,
        });
        setLocalOrders((prev) => ({
          ...prev,
          [source.droppableId]: sourceColumn,
          [destination.droppableId]: destColumn,
        }));
        openSnackbar('Ocurrió un error, intentalo denuevo', 'error');
      }
    } else {
      const column = localOrders[source.droppableId];
      const copiedItems = [...column];
      const [removedOrder] = copiedItems.splice(source.index, 1);
      const updatedOrder = {
        ...removedOrder,
        status: destination.droppableId as OrderStatus,
      };
      copiedItems.splice(destination.index, 0, updatedOrder);

      setLocalOrders((prev) => ({
        ...prev,
        [source.droppableId]: copiedItems,
      }));

      try {
        //UPDATE ORDER STATUS
        await updateOrderStatus({
          organizationId,
          orderId: updatedOrder.id,
          status: updatedOrder.status,
        });
      } catch (error) {
        const [removedOrder] = copiedItems.splice(destination.index, 1);

        const updatedOrder = {
          ...removedOrder,
          status: source.droppableId as OrderStatus,
        };

        copiedItems.splice(source.index, 0, updatedOrder);

        setLocalOrders((prev) => ({
          ...prev,
          [source.droppableId]: copiedItems,
        }));
        openSnackbar('Ocurrió un error, intentalo denuevo', 'error');
      }
    }
  };

  const activeOrder = useMemo(() => {
    if (!orders || !orderId) return;
    return orders.find((o: IOrder) => o.id === orderId);
  }, [orderId, orders]) as IOrder;

  useEffect(() => {
    if (!activeOrder?.createdBy) return;
    dispatch(fetchUsers([activeOrder.createdBy]));
  }, [activeOrder?.createdBy, dispatch]);

  useEffect(() => {
    if (orders) {
      const draft = orders.reduce((acc, curr: IOrder) => {
        return {
          ...acc,
          [curr.status]: [...(acc[curr.status] || []), curr],
        } as StageOrders;
      }, {});

      setLocalOrders(draft);
    }
  }, [orders]);

  useEffect(() => {
    dispatch(setContainer('fluid'));
    return () => {
      dispatch(setContainer('fixed'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Stack gap={2} height={'100%'} pb={2}>
        <PageHeader
          title="Order"
          action={
            <Button color="success" onClick={() => addOrder(organizationId)}>
              Crear Orden
            </Button>
          }
        />
        {/*  */}
        <Grid container direction={'row'} spacing={1.5} sx={{ height: '100%' }}>
          {Object.entries(STAGES).map(([stageId, stage]) => (
            <Grid key={stageId} size={{ xs: 3 }}>
              <Paper sx={{ height: '100%' }}>
                <OrderStageHeader
                  title={stage.title}
                  icon={stage.icon}
                  quantity={0}
                />
                <Droppable droppableId={stageId}>
                  {(provided, snapshot) => {
                    // sorted
                    const sortedOrders =
                      localOrders[stageId]?.sort((a, b) => {
                        return a.createdAt - b.createdAt;
                      }) || [];
                    return (
                      <Stack
                        spacing={2}
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{
                          padding: 12,
                          background: snapshot.isDraggingOver
                            ? 'lightblue'
                            : 'transparent',
                        }}
                      >
                        {!isEmpty(localOrders)
                          ? sortedOrders.map((order, index) => (
                              <div key={order.id}>
                                <Draggable draggableId={order.id} index={index}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={{
                                        ...getItemStyle(
                                          snapshot.isDragging,
                                          provided.draggableProps.style
                                        ),
                                      }}
                                    >
                                      <CardStage {...order} />
                                    </div>
                                  )}
                                </Draggable>
                              </div>
                            ))
                          : null}
                        {provided.placeholder}
                      </Stack>
                    );
                  }}
                </Droppable>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Stack>
      <OrderDetails {...activeOrder} />
    </DragDropContext>
  );
};

export default Orders;
